import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { useDialog, useRecordEvent } from "app/hook";
import { DialogType, AnalyticsEventType } from "app/types";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { ParentGate } from "features/parent";
import { AVAILABLE_UPGRADE_PLANS } from "features/user/constants";
import { PlanCategory, BillingPeriod, usePlan, useUpgradePurchase } from "features/subscription";
import {
  UserPlanCurrentUsageCard,
  UserPlanUpgradeCta,
  UserPlanWarningDialogs,
} from "features/user/components";

const PANEL_WIDTH = 1180;
const PANEL_HEIGHT = 800;

export const UpgradePromotion = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const handleRecordEvent = useRecordEvent();
  const upgradePromotion = useSelector((state: RootState) => state.config.dialog.upgrade_promotion);
  const plan = usePlan();
  const upgrade = useUpgradePurchase();

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [parentGateVisible, setParentGateVisible] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<PlanCategory | null>(null);

  const availablePlans = AVAILABLE_UPGRADE_PLANS[plan] ?? [];
  // 案内を開いたところ（GA の source）
  const source = upgradePromotion.args?.source ?? "unknown";

  useEffect(() => {
    if (upgradePromotion.value) {
      handleDialog({ type: DialogType.RESTRICTED_FEATURE, value: false });
      handleRecordEvent(AnalyticsEventType.UPGRADE_PROMPT_SHOW, { source });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradePromotion.value]);

  const handleClose = () => {
    handleRecordEvent(AnalyticsEventType.UPGRADE_PROMPT_CLOSE, { source });
    btnDelay(() => {
      play();
      handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: false });
      setPendingPurchase(null);
    });
  };

  const handleUpgradeClick = (category: PlanCategory) => {
    handleRecordEvent(AnalyticsEventType.UPGRADE_PLAN_SELECT, {
      plan_category: category,
      billing_period: billingPeriod,
    });
    setPendingPurchase(category);
    setParentGateVisible(true);
  };

  const handleParentGateConfirm = () => {
    setParentGateVisible(false);
    if (pendingPurchase) {
      upgrade.purchase(pendingPurchase, billingPeriod, source);
      setPendingPurchase(null);
    }
  };

  const handleParentGateClose = () => {
    setParentGateVisible(false);
    setPendingPurchase(null);
  };

  const getPriceString = (category: PlanCategory, period: BillingPeriod): string =>
    upgrade.getPriceDisplay(category, period).priceString;

  return (
    <>
      <Dialog
        fullScreen
        maxWidth={false}
        sx={{ zIndex: 999999999 }}
        open={upgradePromotion.value}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: { style: { backgroundColor: "transparent" } },
        }}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div
          className="flex-col-el flex-center relative w-full h-full"
          style={{
            filter:
              "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
          }}
        >
          <div
            className="flex-col-el flex-center"
            style={{
              width: PANEL_WIDTH * scale,
              height: PANEL_HEIGHT * scale,
            }}
          >
            <div
              className="flex-col-el flex-center origin-center"
              style={{
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                transform: `scale(${scale})`,
              }}
            >
              <CloseBtn close={handleClose} />
              <div className="flex-col-el w-full h-full rounded-lg bg-beige-60 shadow-[0_12px_40px_rgba(63,74,97,0.12),0_4px_12px_rgba(63,74,97,0.08)] border-[8px] border-solid border-white">
                <div className="flex-col-el flex-1 min-h-0 w-full p-6 gap-4">
                  <div className="flex-col-el items-center justify-center w-full shrink-0 gap-1">
                    <p className="type-text type-hero text-textcolor-black leading-tight">
                      {I18n.t("MSG_UPGRADE_MODAL_TITLE")}
                    </p>
                    <p className="type-text type-md text-gray2-100 leading-tight">
                      {I18n.t("MSG_UPGRADE_MODAL_SUBTITLE")}
                    </p>
                  </div>
                  <UserPlanUpgradeCta
                    currentPlan={plan}
                    availablePlans={availablePlans}
                    billingPeriod={billingPeriod}
                    getPriceString={getPriceString}
                    onChangeBillingPeriod={setBillingPeriod}
                    onUpgrade={handleUpgradeClick}
                    includeFreePlan
                    leadingCard={<UserPlanCurrentUsageCard />}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ParentGate
        visible={parentGateVisible}
        confirm={handleParentGateConfirm}
        close={handleParentGateClose}
      />

      <UserPlanWarningDialogs
        showVersionWarning={upgrade.showVersionWarning}
        onCloseVersionWarning={upgrade.closeVersionWarning}
        onGotoStoreUpdate={upgrade.gotoStoreUpdate}
      />
    </>
  );
};
